import { useLocation } from '@remix-run/react';
import { useAuth } from '~/lib/auth-context';
import type { User } from '@supabase/supabase-js';
import { HeaderUI } from './HeaderUI';
import { useSubscriptionTier } from '~/hooks/profile/useSubscriptionTier';
import { SubscriptionTier } from '~/queries/subscription';
import { AvatarDropdown } from '~/design-system/components/AvatarDropdown/AvatarDropdown';
import { Logo } from '~/design-system/elements/Logo/Logo';
import { UpgradeButton } from '~/design-system/components/UpgradeButton/UpgradeButton';
import { DeckVisibility } from '~/types/project';
import styles from './Header.module.css';

type HeaderBackgroundVariant = 'card' | 'subtle' | 'muted' | 'transparent';

interface HeaderProps {
  serverUser?: User | null;
  disabled?: boolean;
  minimal?: boolean;
  title?: string;
  user?: User | null;
  simplified?: boolean;
  backgroundVariant?: HeaderBackgroundVariant;
  showAvatar?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  deckId?: string;
  projectId?: string;
  conversationId?: string;
  currentVisibility?: DeckVisibility;
  isAuthenticated?: boolean;
  publicStory?: {
    title: string;
    createdAt: string;
    author: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      position?: string;
      avatar?: string;
    };
    showAnnotations: boolean;
    onToggleAnnotations: () => void;
  };
}

export default function Header(props: HeaderProps) {
  const { user: authUser, isLoading: authIsLoading } = useAuth();
  const location = useLocation();
  const currentPlan = useSubscriptionTier();
  
  const displayUser = props.serverUser !== undefined ? props.serverUser : authUser;
  const isLoading = authIsLoading && props.serverUser === undefined;
  const isStoryPage = location.pathname.startsWith("/deck");
  const showUpgradeButton = !!(currentPlan && currentPlan !== SubscriptionTier.MAX);

  return (
    <HeaderUI
      displayUser={displayUser}
      isLoading={isLoading}
      minimal={props.minimal}
      simplified={props.simplified}
      showAvatar={props.showAvatar}
      backgroundVariant={props.backgroundVariant || 'transparent'}
      isStoryPage={isStoryPage}
      showUpgradeButton={showUpgradeButton}
      showBackButton={props.showBackButton}
      onBackClick={props.onBackClick}
      avatarComponent={<AvatarDropdown />}
      logoComponent={<Logo size={32} withLink={true} className={styles.logoContainer} />}
      upgradeButtonComponent={<UpgradeButton />}
      deckId={props.deckId}
      projectId={props.projectId}
      conversationId={props.conversationId}
      currentVisibility={props.currentVisibility}
      isAuthenticated={props.isAuthenticated}
      publicStory={props.publicStory}
    />
  );
} 